import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');
  const category = getCategoryBySlug(activeCategory);
  const articles = getArticlesByCategory(activeCategory);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <Breadcrumb
          items={[
            { label: '分类探索', href: '/category/ai-qing-yu-qin-mi' },
            ...(category ? [{ label: category.name }] : []),
          ]}
        />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="container-custom">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-100 mb-8">
              分类探索
            </h1>

            <div className="flex flex-wrap gap-2 md:gap-3 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-100 text-foreground-400 hover:text-primary-400 border border-background-300/50'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {category && (
              <div className="mb-10">
                <div className="relative h-[200px] md:h-[280px] rounded-lg overflow-hidden mb-6">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${category.image})` }}
                    role="img"
                    aria-label={category.name}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-100 mb-2">
                      {category.name}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-400 max-w-2xl">
                      {category.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="card-dark group cursor-pointer overflow-hidden"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden rounded-md mb-4">
                      <div
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                        style={{ backgroundImage: `url(${article.image})` }}
                        role="img"
                        aria-label={article.title}
                      />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-200 font-heading mb-2 group-hover:text-primary-400 transition-colors duration-300 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-600">
                      <span>{article.author}</span>
                      <div className="flex items-center gap-3">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-600">
                  <i className="ri-inbox-line text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}